import { useCallback, useEffect, useRef, useState } from 'react';
import {
  XIcon,
  ZapIcon,
  BarcodeIcon,
  CheckIcon,
  PlusIcon,
  MinusIcon,
  PackageIcon,
} from '../../components/icons';
import { useLang } from '../../lib/language';
import { namePair } from '../../lib/localizedName';
import { fmt } from '../../lib/mock-data';

/**
 * Camera barcode scanner.
 *
 * The button in the search bar used to only raise a toast ("scanner ready") —
 * that was written for hardware wedge scanners, which type into the focused
 * input and need no UI at all. On a phone or tablet there is no wedge, so the
 * button has to actually open the camera. This does that.
 *
 * Two decoders, picked at runtime:
 *   1. `BarcodeDetector` — native, hardware-accelerated, present on Chrome for
 *      Android and desktop Chrome/Edge. This is what the POS tablets run.
 *   2. `@zxing/browser` — pure-JS fallback, lazily imported so its ~250KB only
 *      lands on the devices that need it (iOS Safari, Firefox).
 *
 * ── One barcode, one line ────────────────────────────────────────────────
 * Scanning used to run continuously, with a 1.5s window that ignored a repeat
 * of the same code. That is the wrong shape for a counter: a barcode sitting
 * in frame for two seconds became two cart lines, and the cashier had no say
 * in the quantity. The modal is now a small state machine:
 *
 *   scanning → (decode) → confirm  → (Add) → added → (Scan next) → scanning
 *                          ↑ camera frozen, decoder gated ↑
 *
 * A decode flips `gateRef` synchronously — before any React state update — so
 * every frame that lands while the confirm sheet is open is dropped rather
 * than queued. The camera stream stays open the whole time (reacquiring it
 * costs a second and, on iOS, a permission flicker); only the video element is
 * paused, which doubles as the visual cue that scanning has stopped.
 *
 * Resolution and cart writes belong to the caller: `lookup` turns a code into
 * an item preview (or reports a miss / a hand-off), `onAdd` commits the chosen
 * quantity. The modal never touches the cart itself.
 */

/** What a decoded code resolved to, as far as this modal needs to know. */
export type ScannedItem = {
  id: number;
  name: string;
  nameAr?: string | null;
  /** Unit price, in tenant currency. */
  price: number;
  code?: string | null;
  /**
   * The unit whose barcode was scanned, for a multi-unit item — a carton's
   * barcode adds a carton, a can's barcode adds a can. Names are set only for
   * a multi-unit item, and are shown after the price on the sheet.
   */
  unitId?: number | null;
  unitName?: string | null;
  unitNameAr?: string | null;
  /** Data URL for the item photo, when the tenant shows product images. */
  image?: string | null;
  /** Quantity of this item already on the cart, shown as context. */
  inCart?: number;
  /**
   * Inv_Items.AllowDecimals — sold by weight / fraction. Opens the quantity
   * to 0.001; without it a typed fraction rounds to whole units (floor 1).
   */
  allowDecimals?: boolean;
};

export type ScanLookup =
  /**
   * Resolved — the modal asks for a quantity, then calls `onAdd`.
   *
   * `qty` pre-fills that field. It is set only when the code itself carried a
   * quantity (an embedded "scale" barcode); the cashier can still change it,
   * which is the point of stopping here rather than adding straight away.
   */
  | { status: 'found'; item: ScannedItem; qty?: number }
  /**
   * The caller has taken the code over (an item with modifiers opens its own
   * modal, which cannot sit on top of a live camera). The modal closes itself.
   */
  | { status: 'handled' }
  /** Nothing matched — the modal shows the miss and offers another scan. */
  | { status: 'not-found'; message?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Resolve a decoded code. Must be a lookup, not a mutation — it runs on the
   * decode path and may be called for codes the cashier then cancels.
   */
  lookup: (code: string) => ScanLookup;
  /** Commit: add `qty` of the resolved item to the cart. */
  onAdd: (item: ScannedItem, qty: number) => void;
  /**
   * Offered on a miss ("Search this code"). When given, choosing it closes the
   * scanner and hands the raw code back for the search box.
   */
  onSearch?: (code: string) => void;
};

/** Camera lifecycle. Orthogonal to `phase`, which is the interaction state. */
type Status = 'starting' | 'scanning' | 'error';
/** What the cashier is being asked for right now. */
type Phase = 'scanning' | 'confirm' | 'added' | 'not-found';

/** Formats worth asking for. Anything the device does not support is dropped. */
const WANTED_FORMATS = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'code_93',
  'itf',
  'codabar',
  'qr_code',
  'data_matrix',
];

/** Quantities worth one tap. Anything else goes through the stepper or field. */
const QTY_CHIPS = [1, 2, 3, 5, 10];

/**
 * How long the just-scanned code stays blocked after scanning resumes. Long
 * enough for the cashier to move the item out of frame, short enough that
 * deliberately scanning the same barcode twice still works.
 */
const RESUME_BLOCK_MS = 1200;

const MAX_QTY = 9999;

/** A short confirmation blip. Cheap enough to build per scan. */
function beep(kind: 'hit' | 'miss' = 'hit'): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    // A miss gets a lower, longer tone — distinguishable across a noisy shop
    // without the cashier having to look at the screen.
    osc.frequency.value = kind === 'hit' ? 1720 : 320;
    const dur = kind === 'hit' ? 0.12 : 0.26;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur + 0.01);
    osc.onended = () => void ctx.close();
  } catch {
    /* audio is a nicety — never let it break a scan */
  }
}

/**
 * Parse a typed quantity. Blank / garbage → null, so callers can hold fire.
 * A whole-unit item rounds a typed fraction to the nearest unit (never below
 * 1); a by-weight item keeps 3 decimals, matching the cart and the
 * DECIMAL(18,3) column behind it.
 */
function parseQty(text: string, allowDecimals: boolean): number | null {
  const n = Number(text.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded = allowDecimals ? Math.round(n * 1000) / 1000 : Math.max(1, Math.round(n));
  return Math.min(MAX_QTY, rounded);
}

export function BarcodeScannerModal({
  open,
  onClose,
  lookup,
  onAdd,
  onSearch,
}: Props): JSX.Element | null {
  const { t, lang } = useLang();

  const videoRef = useRef<HTMLVideoElement>(null);
  const qtyInputRef = useRef<HTMLInputElement>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);

  /**
   * Decoder gate. Set synchronously on the decode path (React state lands a
   * frame or more later, and the detector runs every 150ms), so this — not
   * `phase` — is what actually stops the second read.
   */
  const gateRef = useRef(false);
  /** Last accepted code + when scanning resumed after it, for the re-arm block. */
  const lastHit = useRef<{ code: string; resumedAt: number }>({ code: '', resumedAt: 0 });

  // These change identity every render; refs keep the camera effect from
  // tearing down and restarting the stream on each parent re-render.
  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [status, setStatus] = useState<Status>('starting');
  const [errorText, setErrorText] = useState('');
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [torchOn, setTorchOn] = useState(false);
  const [torchable, setTorchable] = useState(false);
  const [manual, setManual] = useState('');
  const [flash, setFlash] = useState(false);

  const [phase, setPhase] = useState<Phase>('scanning');
  const [pending, setPending] = useState<ScannedItem | null>(null);
  /** Held as text so the field can be cleared and decimals typed freely. */
  const [qtyText, setQtyText] = useState('1');
  const [lastAdded, setLastAdded] = useState<{ item: ScannedItem; qty: number } | null>(null);
  const [missCode, setMissCode] = useState('');
  const [missText, setMissText] = useState('');
  /** Running tally for this scanner session — lines added and units. */
  const [session, setSession] = useState({ lines: 0, units: 0 });

  const allowDecimals = !!pending?.allowDecimals;
  const qty = parseQty(qtyText, allowDecimals);

  /** Freeze the preview on the last frame — the "scanning is off" cue. */
  const freeze = useCallback(() => {
    gateRef.current = true;
    videoRef.current?.pause();
  }, []);

  /** Re-arm the decoder and un-freeze the preview. */
  const resume = useCallback(() => {
    lastHit.current = { code: lastHit.current.code, resumedAt: Date.now() };
    setPending(null);
    setMissCode('');
    setMissText('');
    setPhase('scanning');
    gateRef.current = false;
    const video = videoRef.current;
    if (video) void video.play().catch(() => undefined);
  }, []);

  /**
   * A decoded (or typed) code. Gated so exactly one read gets through per
   * confirm cycle.
   */
  const accept = useCallback((raw: string) => {
    if (gateRef.current) return;
    const code = raw.trim();
    if (!code) return;

    // Re-arm block: the item the cashier just added is usually still under the
    // lens when scanning resumes.
    const hit = lastHit.current;
    if (code === hit.code && Date.now() - hit.resumedAt < RESUME_BLOCK_MS) return;

    // Shut the decoder down before anything async or stateful happens.
    freeze();
    lastHit.current = { code, resumedAt: 0 };

    setFlash(true);
    window.setTimeout(() => setFlash(false), 160);
    navigator.vibrate?.(60);

    const result = lookupRef.current(code);

    if (result.status === 'handled') {
      beep('hit');
      onCloseRef.current();
      return;
    }

    if (result.status === 'not-found') {
      beep('miss');
      setMissCode(code);
      setMissText(result.message ?? '');
      setPhase('not-found');
      return;
    }

    beep('hit');
    setPending(result.item);
    setQtyText(String(result.qty ?? 1));
    setPhase('confirm');
  }, [freeze]);

  // Camera + decoder lifecycle. Restarts when the modal opens or the camera
  // is flipped; everything it allocates is released in the cleanup.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    let stream: MediaStream | null = null;
    let loopTimer = 0;
    let zxingControls: { stop: () => void } | null = null;

    const fail = (msg: string): void => {
      if (cancelled) return;
      setStatus('error');
      setErrorText(msg);
    };

    void (async () => {
      setStatus('starting');
      setErrorText('');
      setTorchOn(false);
      setTorchable(false);

      // getUserMedia is only exposed on https:// and localhost. Without this
      // check the failure reads as a generic "camera unavailable".
      if (!window.isSecureContext) {
        fail(
          t({
            en: 'The camera needs a secure connection (https). Open this terminal over https and try again.',
            ar: 'تحتاج الكاميرا إلى اتصال آمن (https). افتح هذا الجهاز عبر https وحاول مرة أخرى.',
          }),
        );
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        fail(
          t({
            en: 'This browser cannot open the camera. Use a USB or Bluetooth scanner, or type the barcode below.',
            ar: 'لا يستطيع هذا المتصفح فتح الكاميرا. استخدم ماسحاً عبر USB أو بلوتوث، أو اكتب الباركود أدناه.',
          }),
        );
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (err) {
        const name = (err as { name?: string } | null)?.name ?? '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          fail(
            t({
              en: 'Camera permission was denied. Allow camera access for this site in your browser settings, then reopen the scanner.',
              ar: 'تم رفض إذن الكاميرا. اسمح بالوصول إلى الكاميرا لهذا الموقع من إعدادات المتصفح ثم أعد فتح الماسح.',
            }),
          );
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          fail(
            t({
              en: 'No camera was found on this device.',
              ar: 'لم يتم العثور على كاميرا في هذا الجهاز.',
            }),
          );
        } else if (name === 'NotReadableError') {
          fail(
            t({
              en: 'The camera is already in use by another app. Close it and try again.',
              ar: 'الكاميرا مستخدمة بواسطة تطبيق آخر. أغلقه وحاول مرة أخرى.',
            }),
          );
        } else {
          fail(
            t({ en: 'Could not start the camera.', ar: 'تعذّر تشغيل الكاميرا.' }),
          );
        }
        return;
      }

      const video = videoRef.current;
      if (cancelled || !video) return;

      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        /* autoplay can reject while the element is still mounting */
      }
      if (cancelled) return;

      const track = stream.getVideoTracks()[0] ?? null;
      trackRef.current = track;
      // `torch` is not in the standard MediaTrackCapabilities type yet.
      const caps = (track?.getCapabilities?.() ?? {}) as Record<string, unknown>;
      setTorchable('torch' in caps);
      setStatus('scanning');

      const NativeDetector = (
        window as unknown as { BarcodeDetector?: new (opts?: { formats?: string[] }) => {
          detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
        } }
      ).BarcodeDetector;

      if (NativeDetector) {
        let formats = WANTED_FORMATS;
        try {
          const supported = await (
            NativeDetector as unknown as { getSupportedFormats?: () => Promise<string[]> }
          ).getSupportedFormats?.();
          if (supported?.length) formats = WANTED_FORMATS.filter((f) => supported.includes(f));
        } catch {
          /* fall through with the full wish-list */
        }
        if (cancelled) return;

        const detector = new NativeDetector(formats.length > 0 ? { formats } : undefined);
        const tick = async (): Promise<void> => {
          if (cancelled) return;
          // Skip the decode entirely while the confirm sheet is up: the frame
          // is frozen, so detecting it would only burn battery.
          // A paused / zero-sized video makes detect() throw on some builds.
          if (!gateRef.current && video.readyState >= 2 && video.videoWidth > 0) {
            try {
              const found = await detector.detect(video);
              const raw = found[0]?.rawValue;
              if (raw) accept(raw);
            } catch {
              /* transient decode failures are normal between frames */
            }
          }
          if (!cancelled) loopTimer = window.setTimeout(() => void tick(), 150);
        };
        void tick();
        return;
      }

      // No native detector (iOS Safari, Firefox) — pull in ZXing.
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader(undefined, {
          delayBetweenScanAttempts: 150,
        });
        zxingControls = await reader.decodeFromVideoElement(video, (result) => {
          // ZXing keeps calling back off its own timer; `accept` gates, but
          // checking here skips the work too.
          if (result && !gateRef.current) accept(result.getText());
        });
        if (cancelled) zxingControls.stop();
      } catch {
        fail(
          t({
            en: 'The barcode decoder failed to load. Type the barcode below instead.',
            ar: 'تعذّر تحميل مفكك الباركود. اكتب الباركود أدناه بدلاً من ذلك.',
          }),
        );
      }
    })();

    return () => {
      cancelled = true;
      if (loopTimer) window.clearTimeout(loopTimer);
      zxingControls?.stop();
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.srcObject = null;
      }
      stream?.getTracks().forEach((tr) => tr.stop());
      trackRef.current = null;
    };
    // `t` is recreated per language change; keying on it would restart the
    // camera on a language toggle for no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing, accept]);

  // Reset transient state each time the modal is opened.
  useEffect(() => {
    if (!open) return;
    setManual('');
    setPhase('scanning');
    setPending(null);
    setLastAdded(null);
    setMissCode('');
    setMissText('');
    setQtyText('1');
    setSession({ lines: 0, units: 0 });
    gateRef.current = false;
    lastHit.current = { code: '', resumedAt: 0 };
  }, [open]);

  // Put the caret in the quantity field as soon as it appears, with the value
  // selected so typing "3" replaces the 1 instead of making it 13.
  useEffect(() => {
    if (phase !== 'confirm') return;
    const el = qtyInputRef.current;
    if (!el) return;
    // A frame's delay — the field is mounting in this same commit.
    const id = window.setTimeout(() => {
      el.focus();
      el.select();
    }, 30);
    return () => window.clearTimeout(id);
  }, [phase]);

  const commit = useCallback(() => {
    if (!pending || !qty) return;
    onAddRef.current(pending, qty);
    setLastAdded({ item: pending, qty });
    setSession((s) => ({ lines: s.lines + 1, units: Math.round((s.units + qty) * 1000) / 1000 }));
    setPending(null);
    setPhase('added');
  }, [pending, qty]);

  // Escape steps back one level rather than always closing: from the confirm
  // sheet it means "cancel this item", not "abandon the scan session".
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (phase === 'confirm' || phase === 'not-found') resume();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, phase, resume]);

  async function toggleTorch(): Promise<void> {
    const track = trackRef.current;
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({
        advanced: [{ torch: next } as unknown as MediaTrackConstraintSet],
      });
      setTorchOn(next);
    } catch {
      setTorchable(false);
    }
  }

  function bumpQty(delta: number): void {
    const base = qty ?? 1;
    // Whole-unit steps. A by-weight item already below 1 stays put on "-"
    // rather than dropping to 0.001 — fractions are typed, not stepped.
    const floor = allowDecimals ? Math.min(1, base) : 1;
    const next = Math.min(MAX_QTY, Math.max(floor, Math.round((base + delta) * 1000) / 1000));
    setQtyText(String(next));
  }

  if (!open) return null;

  const paused = phase !== 'scanning';
  const itemName = (it: ScannedItem) => it.name || (it.code ? `#${it.code}` : '');

  return (
    <div
      className="modal-overlay show scanner-overlay"
      // While a decision is pending, a stray tap outside would throw the
      // scanned item away. Only the idle states close on the backdrop.
      onClick={() => {
        if (phase === 'scanning' || phase === 'added') onClose();
      }}
    >
      <div className="modal scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="scanner-header">
          <div className="scanner-title">
            <BarcodeIcon size={18} />
            <span>{t({ en: 'Scan barcode', ar: 'مسح الباركود' })}</span>
          </div>
          {session.lines > 0 && (
            <div className="scanner-tally" aria-live="polite">
              {t({ en: 'Added', ar: 'أُضيف' })} {session.lines}
            </div>
          )}
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className={`scanner-stage${flash ? ' flash' : ''}${paused ? ' paused' : ''}`}>
          <video
            ref={videoRef}
            className="scanner-video"
            playsInline
            muted
            autoPlay
            /* The preview is decoration; the decoded value is the content. */
            aria-hidden="true"
          />

          {status === 'scanning' && phase === 'scanning' && (
            <div className="scanner-reticle" aria-hidden="true">
              <span className="scanner-laser" />
            </div>
          )}

          {status === 'starting' && (
            <div className="scanner-state">
              {t({ en: 'Starting camera…', ar: 'جارٍ تشغيل الكاميرا…' })}
            </div>
          )}

          {status === 'error' && phase === 'scanning' && (
            <div className="scanner-state error" role="alert">
              {errorText}
            </div>
          )}

          {/* ── Quantity sheet ─────────────────────────────────────────── */}
          {phase === 'confirm' && pending && (
            <div className="scan-sheet" role="dialog" aria-label={t({ en: 'Quantity', ar: 'الكمية' })}>
              <div className="scan-sheet-item">
                {pending.image ? (
                  <div
                    className="scan-sheet-thumb scan-sheet-thumb--photo"
                    style={{ backgroundImage: `url(${pending.image})` }}
                  />
                ) : (
                  <div className="scan-sheet-thumb">
                    <PackageIcon size={22} />
                  </div>
                )}
                <div className="scan-sheet-meta">
                  {(() => {
                    const n = namePair(lang, { name: pending.name, name_ar: pending.nameAr }, itemName(pending));
                    return (
                      <>
                        <div className="scan-sheet-name" dir="auto">{n.primary}</div>
                        {n.secondary && <div className="scan-sheet-name-ar" dir="auto">{n.secondary}</div>}
                      </>
                    );
                  })()}
                  <div className="scan-sheet-sub">
                    {fmt(pending.price)}
                    {pending.unitName || pending.unitNameAr
                      ? ` / ${(lang === 'ar' ? pending.unitNameAr : pending.unitName) || pending.unitName || pending.unitNameAr}`
                      : ''}
                    {pending.code ? ` · ${pending.code}` : ''}
                    {pending.inCart
                      ? ` · ${t({ en: 'in cart', ar: 'في السلة' })} ${pending.inCart}`
                      : ''}
                  </div>
                </div>
              </div>

              <div className="scan-qty">
                <button
                  type="button"
                  className="scan-qty-btn"
                  onClick={() => bumpQty(-1)}
                  disabled={(qty ?? 1) <= 1}
                  aria-label={t({ en: 'Decrease quantity', ar: 'إنقاص الكمية' })}
                >
                  <MinusIcon size={20} />
                </button>
                <input
                  ref={qtyInputRef}
                  className="scan-qty-input"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={qtyText}
                  onChange={(e) => setQtyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commit();
                    }
                  }}
                  aria-label={t({ en: 'Quantity', ar: 'الكمية' })}
                />
                <button
                  type="button"
                  className="scan-qty-btn"
                  onClick={() => bumpQty(1)}
                  aria-label={t({ en: 'Increase quantity', ar: 'زيادة الكمية' })}
                >
                  <PlusIcon size={20} />
                </button>
              </div>

              <div className="scan-qty-chips">
                {QTY_CHIPS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`scan-chip${qty === n ? ' active' : ''}`}
                    onClick={() => setQtyText(String(n))}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div className="scan-sheet-total">
                <span>{t({ en: 'Line total', ar: 'إجمالي السطر' })}</span>
                <strong>{fmt((qty ?? 0) * pending.price)}</strong>
              </div>
            </div>
          )}

          {/* ── Added confirmation ─────────────────────────────────────── */}
          {phase === 'added' && lastAdded && (
            <div className="scan-sheet scan-sheet--done" role="status">
              <div className="scan-done-mark">
                <CheckIcon size={26} />
              </div>
              <div className="scan-done-text">
                <div className="scan-sheet-name">
                  {lastAdded.qty} × {itemName(lastAdded.item)}
                </div>
                <div className="scan-sheet-sub">
                  {t({ en: 'Added to cart', ar: 'أُضيف إلى السلة' })} ·{' '}
                  {fmt(lastAdded.qty * lastAdded.item.price)}
                </div>
              </div>
            </div>
          )}

          {/* ── Unknown barcode ────────────────────────────────────────── */}
          {phase === 'not-found' && (
            <div className="scan-sheet scan-sheet--miss" role="alert">
              <div className="scan-miss-mark">
                <XIcon size={24} />
              </div>
              <div className="scan-done-text">
                <div className="scan-sheet-name">
                  {t({ en: 'No item with barcode', ar: 'لا يوجد صنف بالباركود' })}
                </div>
                <div className="scan-sheet-code">{missCode}</div>
                {missText && <div className="scan-sheet-sub">{missText}</div>}
              </div>
            </div>
          )}
        </div>

        <div className="scanner-footer">
          {/* The footer is the action bar: which buttons it carries is the
              clearest statement of what the modal expects next. */}
          {phase === 'confirm' && (
            <div className="scanner-actions">
              <button type="button" className="btn-secondary" onClick={resume}>
                {t({ en: 'Cancel', ar: 'إلغاء' })}
              </button>
              <button
                type="button"
                className="btn-primary scan-cta"
                onClick={commit}
                disabled={!qty}
              >
                <CheckIcon size={16} />
                {t({ en: 'Add to cart', ar: 'أضف إلى السلة' })}
              </button>
            </div>
          )}

          {phase === 'added' && (
            <div className="scanner-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                {t({ en: 'Close scanner', ar: 'إغلاق الماسح' })}
              </button>
              <button type="button" className="btn-primary scan-cta" onClick={resume} autoFocus>
                <BarcodeIcon size={16} />
                {t({ en: 'Scan next item', ar: 'مسح صنف آخر' })}
              </button>
            </div>
          )}

          {phase === 'not-found' && (
            <div className="scanner-actions">
              {onSearch && missCode && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    const code = missCode;
                    onClose();
                    onSearch(code);
                  }}
                >
                  {t({ en: 'Search this code', ar: 'ابحث عن هذا الرمز' })}
                </button>
              )}
              <button type="button" className="btn-primary scan-cta" onClick={resume} autoFocus>
                <BarcodeIcon size={16} />
                {t({ en: 'Scan again', ar: 'مسح مرة أخرى' })}
              </button>
            </div>
          )}

          {phase === 'scanning' && (
            <>
              {status === 'scanning' && (
                <div className="scanner-hint">
                  {t({
                    en: 'Hold one barcode inside the frame. Scanning stops on the first read so you can set the quantity.',
                    ar: 'ضع باركود واحداً داخل الإطار. يتوقف المسح عند أول قراءة لتحديد الكمية.',
                  })}
                </div>
              )}

              <div className="scanner-actions">
                {torchable && (
                  <button
                    type="button"
                    className={`btn-secondary${torchOn ? ' active' : ''}`}
                    onClick={() => void toggleTorch()}
                  >
                    <ZapIcon size={16} />
                    {torchOn
                      ? t({ en: 'Light off', ar: 'إطفاء الضوء' })
                      : t({ en: 'Light on', ar: 'تشغيل الضوء' })}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
                  disabled={status === 'error'}
                >
                  {t({ en: 'Flip camera', ar: 'تبديل الكاميرا' })}
                </button>
              </div>

              {/* Always available: the last resort when a barcode is torn, or
                  the camera is blocked at the OS level. Typed codes take the
                  same route, so they get the same quantity prompt. */}
              <form
                className="scanner-manual"
                onSubmit={(e) => {
                  e.preventDefault();
                  const code = manual.trim();
                  if (!code) return;
                  // Bypasses the re-arm block — this is a deliberate entry.
                  lastHit.current = { code: '', resumedAt: 0 };
                  setManual('');
                  accept(code);
                }}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder={t({ en: 'Or type the barcode…', ar: 'أو اكتب الباركود…' })}
                />
                <button type="submit" className="btn-primary" disabled={!manual.trim()}>
                  {t({ en: 'Find', ar: 'بحث' })}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
